import { useContext, useState, useMemo, useCallback } from "react";
import { PageProps } from "../../../models/routing.model";
import UiTable from "../../../components/table/Table.component";
import {
	TableColumnTemplate,
	TableDateMode,
	TableColumn,
} from "../../../models/components/table/table-column.model";
import { TablePagination } from "../../../models/components/table/table-pagination.model";
import { TableMessages } from "../../../models/components/table/table-message.model";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../../models/components/field-message.model";
import { ParticipantDocumentRow } from "../../../models/pages/participant-document.model";
import { IDocument } from "../../../models/entities/document.model";
import { ParticipantContextProps } from "../../../models/contexts/participant-context.model";
import { ParticipantContext } from "../../../contexts/Participant.context";
import { TableTemplateButtonsEvent } from "../../../models/components/table/table-template.model";
import UiButton from "../../../components/button/Button";
import { Formatter } from "../../../services/formatter/formatter.service";
import { useTranslation } from "react-i18next";
import { confirmDialog } from "primereact/confirmdialog";
import { useDocuments } from "../../../hooks/useDocuments";
import UiFieldMessage from "../../../components/field-message/FieldMessage.component";
import axios, { AxiosError } from "axios";
import { useUploadDocument } from "../../../hooks/useUploadDocument";
import { useDeleteDocument } from "../../../hooks/useDeleteDocument";
import ModalDocument from "../../../components/modal-document/ModalDocument.component";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import { getCookieToken } from "../../../services/session/auth.service";

/** Max allowed document upload size: 10 MB */
const MAX_DOCUMENT_UPLOAD_BYTES = 10 * 1024 * 1024;

const ParticipantDocuments = (props: PageProps) => {
	const { t } = useTranslation("common");
	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);
	const participantId = participantContext.participant?.id;

	// Helper function to get MIME type from file name
	const getMimeTypeFromFileName = (fileName: string): string => {
		if (!fileName) return "application/pdf";

		const extension = fileName.split(".").pop()?.toLowerCase() || "";
		const mimeTypes: { [key: string]: string } = {
			pdf: "application/pdf",
			doc: "application/msword",
			docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
			jpg: "image/jpeg",
			jpeg: "image/jpeg",
			png: "image/png",
			gif: "image/gif",
			heic: "image/heic",
		};

		return mimeTypes[extension] || "application/pdf";
	};

	// Hooks
	const documentsQuery = useDocuments({
		participantId: participantId || 0,
	});
	const uploadDocumentMutation = useUploadDocument();
	const deleteDocumentMutation = useDeleteDocument();

	// Modal state
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [editingDocument, setEditingDocument] = useState<IDocument | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [uploadingCount, setUploadingCount] = useState(0);

	/* Table */

	// Pagination (client-side for now, can be made server-side later)
	const [pagination, setPagination] = useState<TablePagination>({
		first: 0,
		rows: 10,
		page: 0,
		sortField: "uploadedOn",
		sortOrder: -1,
		search: undefined,
		filters: undefined,
	});

	// Search
	const [searchValue, setSearchValue] = useState<string | null>(null);

	/**
	 * ACTIONS: Documents
	 * - View
	 * - Edit
	 * - Delete
	 * - Upload
	 */

	const viewOrDownloadDocument = useCallback(
		async (event: TableTemplateButtonsEvent): Promise<void> => {
			const document = (event.row as ParticipantDocumentRow).entity;

			// Determine MIME type from file name (since fileType is always null)
			const mimeType = getMimeTypeFromFileName(document.s3Url!);

			// Otherwise, fetch the document content from the server
			if (!document.id) {
				console.error("Document ID not available");
				return;
			}

			try {
				const response = await EndpointsService.dataRetriever.downloadDocument({
					documentId: document.id,
				});
				// Response is a byte array (Blob) with type 'application/octet-stream'
				const blob = new Blob([response], { type: mimeType });
				if ((event.row as ParticipantDocumentRow).fileTypeIsViewable) {
					Formatter.openFile(blob, mimeType);
				} else {
					Formatter.downloadFile(blob, mimeType, document.s3Url?.split("/").pop()!);
				}
			} catch (error) {
				console.error("Error downloading document from endpoint:", error);
			}
		},
		[]
	);

	const editDocument = useCallback((event: TableTemplateButtonsEvent): void => {
		const document = (event.row as ParticipantDocumentRow).entity;
		setEditingDocument(document);
		setEditModalVisible(true);
	}, []);

	const deleteDocument = useCallback(
		(event: TableTemplateButtonsEvent): void => {
			const document = (event.row as ParticipantDocumentRow).entity;
			confirmDialog({
				message: t("PARTICIPANT.DOCUMENTS.CONFIRM_DELETE", { name: document.name }),
				header: t("PARTICIPANT.DOCUMENTS.CONFIRM_DELETE_HEADER"),
				icon: "pi pi-exclamation-triangle",
				accept: async () => {
					if (participantId && document.id) {
						try {
							await deleteDocumentMutation.mutateAsync({
								documentId: document.id,
							});
						} catch (error) {
							console.error("Error deleting document:", error);
						}
					}
				},
			});
		},
		[t, participantId, deleteDocumentMutation]
	);

	// Columns
	const columns: TableColumn[] = [
		{
			field: "documentName",
			title: "PARTICIPANT.DOCUMENTS.TABLE.COLUMNS.document",
			sortable: true,
			style: {
				maxWidth: "400px",
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
			},
		},
		{
			field: "notes",
			title: "PARTICIPANT.DOCUMENTS.TABLE.COLUMNS.notes",
			sortable: true,
			style: {
				maxWidth: "300px",
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
			},
		},
		{
			field: "uploadedOn",
			title: "PARTICIPANT.DOCUMENTS.TABLE.COLUMNS.uploaded",
			sortable: true,
			template: TableColumnTemplate.DATE,
			templateOptions: {
				dateMode: TableDateMode.UTC,
				dateFormat: {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
					hour: "2-digit",
					minute: "2-digit",
					hour12: true,
				},
			},
		},
		{
			field: "actionsTemplate",
			template: TableColumnTemplate.BUTTONS,
			alignment: "right",
			style: {
				width: "150px",
				minWidth: "150px",
				flexShrink: 0,
			},
		},
	];

	// Convert documents to rows
	const rows = useMemo<ParticipantDocumentRow[]>(() => {
		return (
			documentsQuery.data?.map(
				(item) =>
					new ParticipantDocumentRow(
						item,
						viewOrDownloadDocument,
						editDocument,
						deleteDocument
					)
			) || []
		);
	}, [documentsQuery.data, viewOrDownloadDocument, editDocument, deleteDocument]);

	// Messages
	const messages = new TableMessages();
	const message: UiFieldMessageProps = documentsQuery.isLoading
		? { severity: FieldMessageSeverity.LOADING, label: messages.loading }
		: documentsQuery.isError
			? { severity: FieldMessageSeverity.DANGER, label: messages.error }
			: rows.length === 0
				? { severity: FieldMessageSeverity.INFO, label: messages.empty }
				: { severity: FieldMessageSeverity.INFO, label: "" };

	// Filter rows by search
	const filteredRows = useMemo(() => {
		if (!searchValue) return rows;
		const searchLower = searchValue.toLowerCase();
		return rows.filter(
			(row) =>
				row.documentName.toLowerCase().includes(searchLower) ||
				(row.notes && row.notes.toLowerCase().includes(searchLower))
		);
	}, [rows, searchValue]);

	const uploadDocument = (): void => {
		const input = document.createElement("input");
		input.type = "file";
		input.multiple = true;
		input.accept = "application/pdf,image/*,.doc,.docx";
		input.onchange = async (e: Event) => {
			const target = e.target as HTMLInputElement;
			const files = Array.from(target.files ?? []);
			if (!files.length || !participantId) return;

			const oversized = files.filter((f) => f.size > MAX_DOCUMENT_UPLOAD_BYTES);
			if (oversized.length > 0) {
				setErrorMessage("PARTICIPANT.DOCUMENTS.ERROR_UPLOAD_SIZE");
				return;
			}

			setErrorMessage(null);
			setUploadingCount(files.length);

			const results = await Promise.allSettled(
				files.map((file) =>
					uploadDocumentMutation.mutateAsync({
						participantId,
						file,
						name: file.name.replace(/\.[^/.]+$/, ""),
					})
				)
			);

			setUploadingCount(0);

			const rejected = results.filter(
				(r): r is PromiseRejectedResult => r.status === "rejected"
			);
			if (rejected.length > 0) {
				const firstError = rejected[0].reason;
				if (axios.isAxiosError(firstError) && firstError.response?.status === 409) {
					setErrorMessage("PARTICIPANT.DOCUMENTS.ERROR_UPLOAD_CONFLICT");
				} else if (axios.isAxiosError(firstError) && firstError.response?.status === 413) {
					setErrorMessage("PARTICIPANT.DOCUMENTS.ERROR_UPLOAD_SIZE");
				} else {
					setErrorMessage("PARTICIPANT.DOCUMENTS.ERROR_UPLOAD");
				}
			}
		};
		input.click();
	};

	// Pagination
	const onPagination = async (event: TablePagination): Promise<void> => {
		setPagination({
			...pagination,
			page: event.page,
			rows: event.rows,
			sortField: event.sortField,
			sortOrder: event.sortOrder,
			first: event.first,
		});
	};

	// Search
	const onSearch = (value: string | null | undefined): void => {
		setSearchValue(value || null);
		setPagination({
			...pagination,
			first: 0,
			page: 0,
		});
	};

	const handleEditComplete = () => {
		setEditModalVisible(false);
		setEditingDocument(null);
		documentsQuery.invalidate();
	};

	const handleEditClose = () => {
		setEditModalVisible(false);
		setEditingDocument(null);
	};

	return (
		<>
			<style>{`
				.table-header-title .k-field-message-container {
					word-break: normal !important;
					white-space: nowrap !important;
				}
				.participant-documents-view-download-btn {
					min-width: 7rem;
				}
			`}</style>
			<UiTable
				dataKey="id"
				useAsCard={true}
				customHeader={
					<div
						className="d-flex align-items-center gap-3"
						style={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}
					>
						{props.title && (
							<span
								className="table-title"
								style={{ whiteSpace: "nowrap" }}
							>
								{t(props.title)}
							</span>
						)}
						<UiButton
							type="button"
							className="p-button-rounded p-button-sm"
							icon={"pi pi-upload"}
							label="PARTICIPANT.DOCUMENTS.BUTTON_UPLOAD"
							onClick={uploadDocument}
							disabled={!participantId || uploadingCount > 0}
							loading={uploadingCount > 0}
						/>
						{errorMessage && (
							<span
								style={{
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									flexShrink: 0,
								}}
							>
								<UiFieldMessage
									severity={FieldMessageSeverity.DANGER}
									label={errorMessage}
								/>
							</span>
						)}
					</div>
				}
				columns={columns}
				value={filteredRows}
				message={message}
				isServerPaginated={false}
				pagination={pagination}
				paginationFn={onPagination}
				totalRecords={filteredRows.length}
				search={{
					placeholder: "PARTICIPANT.DOCUMENTS.SEARCH_PLACEHOLDER",
					value: searchValue,
					fn: onSearch,
				}}
			/>

			{/* Edit Document Modal */}
			{editModalVisible && editingDocument && (
				<ModalDocument
					isVisible={editModalVisible}
					document={editingDocument}
					onClose={handleEditClose}
					onComplete={handleEditComplete}
				/>
			)}
		</>
	);
};

export default ParticipantDocuments;
